import "./App.css";
import { useState } from "react";

function App() {
  const [affiliateId, setAffiliateId] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [productName, setProductName] = useState("");
  const [price, setPrice] = useState("");
  const [context, setContext] = useState(
    "Perfect for building the base of your pistachio frappe."
  );
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [cardHtml, setCardHtml] = useState("");
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState("card");

  function buildAffiliateUrl(rawUrl, affiliate) {
    if (!rawUrl) return "";
    const trimmedUrl = rawUrl.trim();
    if (!affiliate) return trimmedUrl;
    try {
      const u = new URL(trimmedUrl);
      u.searchParams.delete("tag");
      u.searchParams.append("tag", affiliate.trim());
      return u.toString();
    } catch (e) {
      const hasQuery = trimmedUrl.includes("?");
      const hasTag = trimmedUrl.includes("tag=");
      if (hasTag) return trimmedUrl;
      return trimmedUrl + (hasQuery ? "&" : "?") + "tag=" + affiliate.trim();
    }
  }

  // Auto-fetch product details from Amazon URL
  async function handleAutoFill() {
    if (!productUrl.trim()) {
      setFetchError("Please paste an Amazon product link first.");
      return;
    }
    setFetching(true);
    setFetchError("");
    try {
      const response = await fetch("/api/fetch-product", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productUrl: productUrl.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        setFetchError(data.error || "Could not fetch product details.");
        return;
      }
      if (data.name) setProductName(data.name);
      if (data.image) setImageUrl(data.image);
      if (data.price) setPrice(data.price);
      if (!data.name && !data.image) {
        setFetchError("Could not extract details. Try filling in manually.");
      }
    } catch (err) {
      console.error(err);
      setFetchError("Something went wrong. Please fill in details manually.");
    } finally {
      setFetching(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setCardHtml("");
    setCopied(false);

    if (mode === "text") {
      try {
        const finalUrl = buildAffiliateUrl(productUrl, affiliateId);
        if (!finalUrl) {
          alert("Please enter a valid Amazon product link.");
          setLoading(false);
          return;
        }
        const linkText = productName || "View on Amazon";
        const html = `<a href="${finalUrl}" target="_blank" rel="nofollow sponsored noopener">${linkText}</a>`;
        setCardHtml(html);
      } catch (err) {
        console.error(err);
        alert("Something went wrong while generating the text link.");
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const response = await fetch("/api/generate-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          affiliateId,
          productUrl,
          imageUrl,
          productName,
          context,
        }),
      });
      const data = await response.json();
      setCardHtml(data.html || "");
    } catch (err) {
      console.error(err);
      alert("Something went wrong while generating the card.");
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!cardHtml) return;
    try {
      await navigator.clipboard.writeText(cardHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error(err);
      alert("Copy did not work. You can still select the text and copy it.");
    }
  }

  return (
    <div className="app-root">
      <header className="app-header">
        <div className="app-header-inner">
          <h1 className="app-title">Amazon Product Card Generator</h1>
          <p className="app-subtitle">
            Turn Amazon links into ready-to-paste product cards or simple text links for your blog.
          </p>
        </div>
      </header>

      <main className="app-main">
        <div className="app-layout">
          <section className="card-form-section">
            <h2 className="section-title">1. Product details</h2>
            <p className="section-subtitle">
              Paste your Amazon link and click Auto-fill, or enter details manually.
            </p>

            <div className="mode-toggle">
              <span className="mode-toggle-label">Output type:</span>
              <label className="mode-toggle-option">
                <input
                  type="radio"
                  name="mode"
                  value="card"
                  checked={mode === "card"}
                  onChange={() => setMode("card")}
                />
                <span>Product card</span>
              </label>
              <label className="mode-toggle-option">
                <input
                  type="radio"
                  name="mode"
                  value="text"
                  checked={mode === "text"}
                  onChange={() => setMode("text")}
                />
                <span>Text link only</span>
              </label>
            </div>

            <form onSubmit={handleSubmit} className="card-form">

              <div className="field">
                <label htmlFor="affiliateId" className="field-label">
                  Amazon affiliate ID
                </label>
                <input
                  id="affiliateId"
                  type="text"
                  value={affiliateId}
                  onChange={(e) => setAffiliateId(e.target.value)}
                  placeholder="foodvloveoc-20"
                  className="field-input"
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="productUrl" className="field-label">
                  Amazon product link
                </label>
                <div className="url-row">
                  <input
                    id="productUrl"
                    type="text"
                    value={productUrl}
                    onChange={(e) => {
                      setProductUrl(e.target.value);
                      setFetchError("");
                    }}
                    placeholder="https://www.amazon.com/dp/..."
                    className="field-input url-input"
                    required
                  />
                  <button
                    type="button"
                    className="autofill-button"
                    onClick={handleAutoFill}
                    disabled={fetching}
                  >
                    {fetching ? "Fetching…" : "⚡ Auto-fill"}
                  </button>
                </div>
                {fetchError && (
                  <p className="fetch-error">{fetchError}</p>
                )}
                {fetching && (
                  <p className="fetch-status">Fetching product details from Amazon…</p>
                )}
              </div>

              {/* Image URL — auto-filled or manual */}
              <div className="field">
                <label htmlFor="imageUrl" className="field-label">
                  Image link{" "}
                  {mode === "text" && (
                    <span className="field-optional">(optional for text link)</span>
                  )}
                  <span className="field-optional"> — auto-filled or paste manually</span>
                </label>
                {imageUrl && (
                  <div className="image-preview-row">
                    <img
                      src={imageUrl}
                      alt="Product preview"
                      className="image-thumb"
                    />
                    <span className="image-preview-label">Preview</span>
                  </div>
                )}
                <input
                  id="imageUrl"
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://m.media-amazon.com/images/..."
                  className="field-input"
                  required={mode === "card"}
                />
              </div>

              <div className="field">
                <label htmlFor="productName" className="field-label">
                  Product name / link text
                  <span className="field-optional"> — auto-filled or edit below</span>
                </label>
                <input
                  id="productName"
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Instant Solo Single Serve Coffee Maker"
                  className="field-input"
                  required
                />
              </div>

              {price && (
                <div className="price-badge">
                  💰 Fetched price: <strong>{price}</strong>
                </div>
              )}

              <div className="field">
                <label htmlFor="context" className="field-label">
                  Short usage line for this blog{" "}
                  {mode === "text" && (
                    <span className="field-optional">(card mode only)</span>
                  )}
                </label>
                <textarea
                  id="context"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  rows={3}
                  className="field-textarea"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="primary-button"
              >
                {loading
                  ? "Generating…"
                  : mode === "card"
                  ? "Generate product card"
                  : "Generate Amazon text link"}
              </button>
            </form>
          </section>

          <section className="output-section">
            <h2 className="section-title">2. Copyable HTML</h2>
            <p className="section-subtitle">
              Paste this into a custom HTML block in WordPress or your editor.
            </p>

            <div className="copy-row">
              <button
                type="button"
                className="secondary-button"
                onClick={handleCopy}
                disabled={!cardHtml}
              >
                {copied ? "Copied ✓" : "Copy HTML"}
              </button>
              {copied && (
                <span className="copy-status">HTML copied to clipboard.</span>
              )}
            </div>

            <textarea
              value={cardHtml}
              readOnly
              rows={10}
              className="code-output"
            />

            <h2 className="section-title section-title-preview">
              3. Live preview
            </h2>
            <div className="preview-wrapper">
              {cardHtml ? (
                <div
                  className="preview-card"
                  dangerouslySetInnerHTML={{ __html: cardHtml }}
                />
              ) : (
                <p className="preview-placeholder">
                  Generate a card or text link to see a live preview here.
                </p>
              )}
            </div>
          </section>
        </div>
      </main>

      <footer className="app-footer">
        <p className="footer-text">
          Built for fast Amazon product cards and text links for FoodvLove and other blogs.
        </p>
      </footer>
    </div>
  );
}

export default App;